// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
import { useCallback, useEffect, useRef, useState } from 'react';

// Long enough to read the confirm and click it, short enough that an armed
// button does not sit waiting for a click somebody has forgotten about
const CONFIRMATION_TIMEOUT_MS = 3000;

/** Arms an action on the first click and runs it on the second. */
export function useClickToConfirm(onConfirm: () => void) {
  const [isPending, setIsPending] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reset = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsPending(false);
  }, []);

  useEffect(() => reset, [reset]);

  const confirm = () => {
    if (isPending) {
      reset();
      onConfirm();
      return;
    }

    setIsPending(true);
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      setIsPending(false);
    }, CONFIRMATION_TIMEOUT_MS);
  };

  return { isPending, confirm, reset };
}
